import type {
  EntitlementDecision,
  MonetizationPlacement,
  PaidPlanTier,
  PlanSet,
  UpgradeContext,
} from '@tabbio/types';

const PENDING_UPGRADE_KEY = 'tabbio:pending-upgrade-context:v1';

export function createUpgradeContext(params: {
  decision: EntitlementDecision;
  placement: MonetizationPlacement;
  entryPoint: string;
  planSet?: PlanSet;
  returnTo?: string;
  draftId?: string;
  resumeToken?: string;
}): UpgradeContext {
  const allowance = params.decision.customerAllowance;
  return {
    placement: params.placement,
    entryPoint: params.entryPoint,
    planSet:
      params.decision.recommendedPlanSet ?? params.planSet ?? 'individual',
    recommendedTier: params.decision.recommendedTier ?? 'plus',
    actionKey: params.decision.actionKey,
    featureKey: params.decision.featureKey,
    reason: params.decision.reasonCode,
    allowanceRemaining: allowance?.remaining,
    allowanceLimit: allowance?.limit,
    allowanceUnit: allowance?.unit,
    resetAt: allowance?.resetAt,
    returnTo: params.returnTo,
    draftId: params.draftId,
    resumeToken: params.resumeToken,
  };
}

export function createChatUpgradeContext(params: {
  decision?: EntitlementDecision | null;
  entryPoint: string;
  planSet?: PlanSet;
  /**
   * Which plan to land the paywall on when there is no server decision to read
   * it from — the model picker knows a Pro-only row needs Pro, not Plus.
   */
  recommendedTier?: PaidPlanTier;
  returnTo?: string;
}): UpgradeContext {
  if (params.decision) {
    return createUpgradeContext({
      decision: params.decision,
      placement: 'chat_limit',
      entryPoint: params.entryPoint,
      planSet: params.planSet,
      returnTo: params.returnTo,
    });
  }
  return {
    placement: 'chat_limit',
    entryPoint: params.entryPoint,
    planSet: params.planSet ?? 'individual',
    recommendedTier: params.recommendedTier ?? 'plus',
    returnTo: params.returnTo,
  };
}

export function getUpgradeAnalyticsProperties(context?: UpgradeContext | null) {
  if (!context) {
    return {};
  }

  return {
    placement: context.placement,
    entryPoint: context.entryPoint,
    actionKey: context.actionKey,
    featureKey: context.featureKey,
    decisionReason: context.reason,
    recommendedTier: context.recommendedTier,
    allowanceRemaining: context.allowanceRemaining,
    allowanceLimit: context.allowanceLimit,
    allowanceUnit: context.allowanceUnit,
    hasReturnPath: Boolean(context.returnTo),
    hasDraft: Boolean(context.draftId),
    hasResumeToken: Boolean(context.resumeToken),
  };
}

export function persistPendingUpgradeContext(context: UpgradeContext) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(PENDING_UPGRADE_KEY, JSON.stringify(context));
  } catch {
    // Checkout must still work when storage is unavailable.
  }
}

export function readPendingUpgradeContext(): UpgradeContext | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(PENDING_UPGRADE_KEY);
    return raw ? (JSON.parse(raw) as UpgradeContext) : null;
  } catch {
    return null;
  }
}

export function clearPendingUpgradeContext() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_UPGRADE_KEY);
  } catch {
    // Best-effort cleanup only.
  }
}

export function getUpgradeContextTranslationKey(
  placement?: MonetizationPlacement,
) {
  switch (placement) {
    case 'chat_limit':
      return 'chatLimit';
    case 'skill_install':
    case 'skill_run':
      return 'skillRun';
    case 'studio_generate':
      return 'studioGenerate';
    case 'nova_preview':
      return 'novaPreview';
    case 'nova_activate':
    case 'nova_run':
      return 'novaActivate';
    case 'export':
      return 'export';
    case 'analytics':
      return 'analytics';
    case 'branding':
      return 'branding';
    case 'connectors':
      return 'connectors';
    case 'voice':
      return 'voice';
    case 'application_autofill':
      return 'applicationAutofill';
    case 'onboarding_ai_choice':
      return 'onboardingChoice';
    case 'onboarding_end':
      return 'onboardingEnd';
    default:
      return 'generic';
  }
}
